import React from 'react';
import {
  FaCalendarAlt,
  FaClock,
  FaEdit,
  FaExclamationCircle,
  FaExclamationTriangle,
  FaFileSignature,
  FaHandHoldingUsd,
  FaHistory,
  FaLayerGroup,
  FaMoneyBillWave,
  FaPhoneAlt,
  FaTrash,
  FaWallet
} from 'react-icons/fa';
import {
  formatearFecha,
  formatearFechaHora,
  formatearMoneda,
  getNivelCriticidad
} from '../../../utilities/gestionMorasUtils';

const TABLE_HEAD_CLASS =
  'border-b border-slate-200 bg-slate-50 text-xs font-semibold uppercase tracking-[0.08em] text-slate-600';
const TEXT_WRAP_ANYWHERE_STYLE = { overflowWrap: 'anywhere' };

export function KpiCard({ title, value, icon, iconStyle }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm md:p-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="mb-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">{title}</p>
          <p className="text-xl font-semibold text-slate-900 md:text-2xl">{value}</p>
        </div>
        <div className={`rounded-xl p-3 ${iconStyle}`}>{icon}</div>
      </div>
    </div>
  );
}

export function GestionMorasMetricCards({ meta }) {
  return (
    <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
      <KpiCard
        title="Préstamos en mora"
        value={meta.total_prestamos || 0}
        icon={<FaLayerGroup className="h-5 w-5" />}
        iconStyle="bg-red-100 text-red-600"
      />
      <KpiCard
        title="Cuotas vencidas"
        value={meta.total_cuotas_vencidas || 0}
        icon={<FaCalendarAlt className="h-5 w-5" />}
        iconStyle="bg-orange-100 text-orange-600"
      />
      <KpiCard
        title="TOTAL DE CAPITAL"
        value={formatearMoneda(meta.total_capital_pendiente || 0)}
        icon={<FaWallet className="h-5 w-5" />}
        iconStyle="bg-sky-100 text-sky-600"
      />
      <KpiCard
        title="TOTAL DE MORA"
        value={formatearMoneda(meta.total_mora || 0)}
        icon={<FaExclamationTriangle className="h-5 w-5" />}
        iconStyle="bg-amber-100 text-amber-600"
      />
      <KpiCard
        title="TOTAL A PAGAR"
        value={formatearMoneda(meta.monto_total || 0)}
        icon={<FaMoneyBillWave className="h-5 w-5" />}
        iconStyle="bg-emerald-100 text-emerald-600"
      />
    </section>
  );
}

export function GestionMorasErrorState({ error, onRetry }) {
  if (!error) {
    return null;
  }

  return (
    <section className="rounded-xl border border-red-200 bg-red-50 p-4 text-red-700">
      <div className="flex items-start gap-3">
        <FaExclamationCircle className="mt-0.5 h-5 w-5" />
        <div>
          <h3 className="text-sm font-semibold">Error al cargar los datos</h3>
          <p className="mt-1 text-sm">{error}</p>
          <button
            type="button"
            onClick={onRetry}
            className="mt-3 rounded-lg border border-red-300 px-3 py-1.5 text-xs font-semibold text-red-700 transition hover:bg-red-100"
          >
            Reintentar
          </button>
        </div>
      </div>
    </section>
  );
}

export function GestionMorasEmptyState({ description }) {
  return (
    <section className="ui-card p-12 text-center">
      <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-emerald-100">
        <FaHandHoldingUsd className="h-10 w-10 text-emerald-500" />
      </div>
      <h3 className="text-xl font-semibold text-slate-900">No se encontraron créditos en mora</h3>
      {description ? <p className="mx-auto mt-2 max-w-xl text-sm text-slate-600">{description}</p> : null}
    </section>
  );
}

export function PrestamoClienteCell({ prestamo }) {
  const criticidad = getNivelCriticidad(prestamo.dias_mora_max || 0);

  return (
    <td className="px-4 py-4">
      <div className="min-w-0">
        <div className="text-sm font-semibold text-slate-900">{prestamo.cliente}</div>
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-600">
          {prestamo.telefono && prestamo.telefono !== '-' ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1">
              <FaPhoneAlt className="text-[10px]" />
              {prestamo.telefono}
            </span>
          ) : null}
          <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1">
            {prestamo.total_cuotas_vencidas} cuota{prestamo.total_cuotas_vencidas === 1 ? '' : 's'} vencida
            {prestamo.total_cuotas_vencidas === 1 ? '' : 's'}
          </span>
          <span className={`inline-flex items-center rounded-full px-2.5 py-1 ${criticidad.badge}`}>
            Mora máxima: {prestamo.dias_mora_max} día{prestamo.dias_mora_max === 1 ? '' : 's'}
          </span>
        </div>
      </div>
    </td>
  );
}

export function PrestamoAsesorCell({ prestamo }) {
  return (
    <td className="px-4 py-4 text-left">
      <div className="text-sm font-semibold text-slate-900">{prestamo.asesor_nombre || 'Sin asesor'}</div>
    </td>
  );
}

export function PrestamoNumeroCreditoCell({ prestamo }) {
  return (
    <td className="px-4 py-4 text-center">
      <div className="text-base font-semibold text-slate-900">#{prestamo.numero_credito}</div>
    </td>
  );
}

export function PrestamoCurrencyCell({
  value,
  className = 'text-slate-900',
  pill = false,
  tdClassName = 'px-4 py-4 text-center'
}) {
  const amount = formatearMoneda(value);

  return (
    <td className={tdClassName}>
      {pill ? (
        <div className={`inline-flex rounded-xl px-3 py-2 text-base font-semibold ${className}`}>{amount}</div>
      ) : (
        <div className={`text-base font-semibold ${className}`}>{amount}</div>
      )}
    </td>
  );
}

export function GestionMorasTable({
  headers,
  prestamos,
  expandedPrestamoId,
  onToggleExpanded,
  detailRowIdPrefix,
  colSpan,
  renderRowCells,
  renderExpandedContent,
  rowInteractionDisabled = false
}) {
  return (
    <section className="ui-card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className={TABLE_HEAD_CLASS}>
              {headers.map((header) => (
                <th key={header.label} className={header.className}>
                  {header.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {prestamos.map((prestamo) => {
              const isExpanded = expandedPrestamoId === prestamo.id_prestamo;
              const detailRowId = `${detailRowIdPrefix}-${prestamo.id_prestamo}`;

              const handleToggleExpanded = () => {
                onToggleExpanded(prestamo.id_prestamo);
              };

              return (
                <React.Fragment key={prestamo.id_prestamo}>
                  <tr
                    tabIndex={rowInteractionDisabled ? -1 : 0}
                    aria-expanded={isExpanded}
                    aria-controls={detailRowId}
                    className={
                      rowInteractionDisabled
                        ? 'transition'
                        : 'cursor-pointer transition hover:bg-slate-50 focus:outline-none focus-visible:bg-slate-50'
                    }
                    onClick={rowInteractionDisabled ? undefined : handleToggleExpanded}
                    onKeyDown={(event) => {
                      if (rowInteractionDisabled) {
                        return;
                      }

                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleToggleExpanded();
                      }
                    }}
                  >
                    {renderRowCells(prestamo)}
                  </tr>

                  {isExpanded ? (
                    <tr id={detailRowId} className="bg-slate-50/70">
                      <td colSpan={colSpan} className="px-4 py-5">
                        {renderExpandedContent(prestamo)}
                      </td>
                    </tr>
                  ) : null}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function UltimoCompromisoCard({
  compromiso,
  title = 'Último compromiso de pago',
  emptyMessage = 'Aún no se ha registrado un compromiso para este préstamo.'
}) {
  return (
    <div className="mb-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">{title}</p>
      {compromiso ? (
        <>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-2 rounded-full bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-700">
              <FaCalendarAlt className="h-3.5 w-3.5" />
              {formatearFecha(compromiso.fecha_compromiso)}
            </span>
            <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-900">
              <FaMoneyBillWave className="h-3.5 w-3.5" />
              {formatearMoneda(compromiso.monto_compromiso)}
            </span>
          </div>
          <p
            className="mt-3 max-w-3xl break-words text-sm leading-6 text-slate-600"
            style={TEXT_WRAP_ANYWHERE_STYLE}
          >
            {compromiso.motivo}
          </p>
        </>
      ) : (
        <p className="mt-2 text-sm text-slate-500">{emptyMessage}</p>
      )}
    </div>
  );
}

export function HistorialCompromisosCard({
  compromisos,
  onEdit,
  onDelete,
  deletingCompromisoId = null,
  disableActions = false
}) {
  const historial = Array.isArray(compromisos) ? compromisos : [];
  const canEdit = typeof onEdit === 'function';
  const canDelete = typeof onDelete === 'function';
  const showActions = canEdit || canDelete;

  return (
    <div className="mb-4 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="flex flex-col gap-2 border-b border-slate-200 px-4 py-4 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Historial de compromisos</p>
        </div>
        <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-700">
          <FaHistory className="text-slate-500" />
          {historial.length} compromiso{historial.length === 1 ? '' : 's'}
        </div>
      </div>

      {historial.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-white text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">
                <th className="px-4 py-3 text-center whitespace-nowrap">Fecha registro</th>
                <th className="px-4 py-3 text-center whitespace-nowrap">Fecha compromiso</th>
                <th className="px-4 py-3 text-center whitespace-nowrap">Monto compromiso</th>
                <th className="px-4 py-3 text-left">Motivo</th>
                {showActions ? <th className="px-4 py-3 text-center whitespace-nowrap">Acciones</th> : null}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {historial.map((compromiso) => (
                <tr key={compromiso.id} className="align-top transition hover:bg-slate-50">
                  <td className="px-4 py-4 text-center text-slate-600 whitespace-nowrap">
                    {formatearFechaHora(compromiso.created_at)}
                  </td>
                  <td className="px-4 py-4 text-center font-semibold text-slate-900 whitespace-nowrap">
                    {formatearFecha(compromiso.fecha_compromiso)}
                  </td>
                  <td className="px-4 py-4 text-center font-semibold text-slate-900 whitespace-nowrap">
                    {formatearMoneda(compromiso.monto_compromiso)}
                  </td>
                  <td className="px-4 py-4 text-slate-700">
                    <p className="max-w-3xl break-words leading-6" style={TEXT_WRAP_ANYWHERE_STYLE}>
                      {compromiso.motivo}
                    </p>
                  </td>
                  {showActions ? (
                    <td className="px-4 py-4 text-center">
                      <div className="flex flex-wrap items-center justify-center gap-2">
                        {canEdit ? (
                          <button
                            type="button"
                            onClick={() => onEdit(compromiso)}
                            disabled={disableActions}
                            className="inline-flex items-center gap-2 rounded-lg bg-amber-400 px-3 py-2 text-sm font-semibold text-amber-950 shadow-sm transition hover:bg-amber-500 disabled:cursor-not-allowed disabled:opacity-60"
                            title="Editar"
                            aria-label="Editar compromiso de pago"
                          >
                            <FaEdit className="h-4 w-4" />
                          </button>
                        ) : null}

                        {canDelete ? (
                          <button
                            type="button"
                            onClick={() => onDelete(compromiso)}
                            disabled={disableActions || deletingCompromisoId === compromiso.id}
                            className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
                            title={deletingCompromisoId === compromiso.id ? 'Eliminando...' : 'Eliminar'}
                            aria-label="Eliminar compromiso de pago"
                          >
                            <FaTrash className="h-4 w-4" />
                          </button>
                        ) : null}
                      </div>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="px-4 py-8 text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-slate-100 text-slate-400">
            <FaFileSignature className="h-6 w-6" />
          </div>
          <p className="mt-3 text-sm font-semibold text-slate-700">Aún no hay compromisos registrados</p>
        </div>
      )}
    </div>
  );
}

export function CuotasVencidasTable({ cuotas, title }) {
  const cuotasVencidas = Array.isArray(cuotas) ? cuotas : [];

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      {title ? (
        <div className="border-b border-slate-200 px-4 py-4">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">{title}</p>
        </div>
      ) : null}
      <div className="overflow-x-auto">
        <table className="min-w-[860px] w-full table-fixed text-sm">
          <colgroup>
            <col className="w-[10%]" />
            <col className="w-[18%]" />
            <col className="w-[14%]" />
            <col className="w-[15%]" />
            <col className="w-[12%]" />
            <col className="w-[15%]" />
            <col className="w-[16%]" />
          </colgroup>
          <thead>
            <tr className="border-b border-slate-200 bg-white text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">
              <th className="px-4 py-3 text-center whitespace-nowrap">N° Cuota</th>
              <th className="px-4 py-3 text-center whitespace-nowrap">Fecha vencimiento</th>
              <th className="px-4 py-3 text-center whitespace-nowrap">Monto cuota</th>
              <th className="px-4 py-3 text-center whitespace-nowrap">Días mora</th>
              <th className="px-4 py-3 text-center whitespace-nowrap">Mora</th>
              <th className="px-4 py-3 text-center whitespace-nowrap">Total pagado</th>
              <th className="px-4 py-3 text-center whitespace-nowrap">Saldo pendiente</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {cuotasVencidas.map((cuota) => {
              const criticidadCuota = getNivelCriticidad(cuota.dias_mora || 0);
              const pagosAplicados = Array.isArray(cuota.pagos) ? cuota.pagos : [];

              return (
                <tr key={cuota.id_cuota} className="align-top transition hover:bg-slate-50">
                  <td className="px-4 py-4 text-center font-semibold text-slate-900">{cuota.numero_cuota}</td>
                  <td className="px-4 py-4 text-center text-slate-700 whitespace-nowrap">
                    {formatearFecha(cuota.fecha_vencimiento)}
                  </td>
                  <td className="px-4 py-4 text-center font-semibold text-slate-900 whitespace-nowrap">
                    {formatearMoneda(cuota.monto_cuota)}
                  </td>
                  <td className="px-4 py-4 text-center">
                    <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${criticidadCuota.badge}`}>
                      <FaClock className="mr-1" />
                      {cuota.dias_mora} días
                    </span>
                  </td>
                  <td className="px-4 py-4 text-center font-semibold text-slate-900 whitespace-nowrap">
                    {formatearMoneda(cuota.monto_mora)}
                  </td>
                  <td className="px-4 py-4 text-center">
                    <div className="font-semibold text-slate-900 whitespace-nowrap">
                      {formatearMoneda(cuota.monto_pagado)}
                    </div>
                    {pagosAplicados[0]?.fecha_pago ? (
                      <div className="mt-1 text-xs text-slate-500 whitespace-nowrap">
                        {formatearFecha(pagosAplicados[0].fecha_pago)}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-4 py-4 text-center">
                    <span className="inline-flex rounded-lg bg-red-50 px-3 py-2 font-semibold text-red-700">
                      {formatearMoneda(cuota.saldo_pendiente)}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
